
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Send, Users, Brain, Lightbulb } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface TeamMember {
  id: string;
  name: string;
  role: string;
  skills: string[];
  compatibility: number;
  avatar: string;
}

export function TeamMatchingAI() {
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [matchedTeam, setMatchedTeam] = useState<TeamMember[]>([]);
  const { toast } = useToast();

  // Sample team members database - in a real implementation, this would come from your API
  const teamMembersDatabase: TeamMember[] = [
    {
      id: '1',
      name: 'Alex Johnson',
      role: 'Frontend Developer',
      skills: ['React', 'TypeScript', 'CSS', 'UI/UX'],
      compatibility: 0,
      avatar: 'https://i.pravatar.cc/150?u=1',
    },
    {
      id: '2',
      name: 'Jordan Lee',
      role: 'Backend Developer',
      skills: ['Node.js', 'Python', 'Database Design', 'API Development'],
      compatibility: 0,
      avatar: 'https://i.pravatar.cc/150?u=2',
    },
    {
      id: '3',
      name: 'Taylor Smith',
      role: 'Project Manager',
      skills: ['Agile', 'Team Leadership', 'Risk Management', 'Stakeholder Communication'],
      compatibility: 0,
      avatar: 'https://i.pravatar.cc/150?u=3',
    },
    {
      id: '4',
      name: 'Morgan Rivera',
      role: 'UX Designer',
      skills: ['User Research', 'Prototyping', 'Figma', 'Accessibility'],
      compatibility: 0,
      avatar: 'https://i.pravatar.cc/150?u=4',
    },
    {
      id: '5',
      name: 'Casey Chen',
      role: 'DevOps Engineer',
      skills: ['CI/CD', 'Docker', 'Kubernetes', 'Cloud Infrastructure'],
      compatibility: 0,
      avatar: 'https://i.pravatar.cc/150?u=5',
    },
    {
      id: '6',
      name: 'Riley Patel',
      role: 'Full Stack Developer',
      skills: ['JavaScript', 'React', 'Node.js', 'MongoDB'],
      compatibility: 0,
      avatar: 'https://i.pravatar.cc/150?u=6',
    },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!prompt.trim()) {
      toast({
        title: "Error",
        description: "Please enter a description of your project requirements.",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      // In a real implementation, this would be an API call to an AI service
      // For demo purposes, we'll use a simple keyword matching algorithm
      const keywords = prompt.toLowerCase().split(/\s+/);
      
      const scoredTeamMembers = teamMembersDatabase.map(member => {
        // Calculate compatibility score based on skill matches
        const skillMatches = member.skills.filter(skill => 
          keywords.some(keyword => skill.toLowerCase().includes(keyword))
        ).length;
        
        // Calculate role relevance score
        const roleRelevance = keywords.some(keyword => 
          member.role.toLowerCase().includes(keyword)
        ) ? 10 : 0;
        
        // Calculate final compatibility score (0-100)
        const compatibility = Math.min(
          100, 
          Math.round(((skillMatches / member.skills.length) * 50) + roleRelevance + Math.random() * 30)
        );
        
        return {
          ...member,
          compatibility
        };
      });
      
      // Sort by compatibility score (highest first)
      const sortedMembers = scoredTeamMembers.sort((a, b) => 
        b.compatibility - a.compatibility
      );
      
      // Wait to simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setMatchedTeam(sortedMembers);
    } catch (error) {
      console.error('Error matching team:', error);
      toast({
        title: "Error",
        description: "Failed to match team members. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const examplePrompts = [
    "I need a team for a React and Node.js project with API development",
    "Looking for UX design experts and frontend developers for a mobile app",
    "Building a DevOps infrastructure with CI/CD pipeline integration",
    "Need project management and backend development for an enterprise solution"
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6" />
            Team Matching AI
          </h1>
          <p className="text-muted-foreground">Find the perfect team for your project needs</p>
        </div>
      </div>
      
      <Tabs defaultValue="match" className="w-full">
        <TabsList className="grid grid-cols-2 w-[400px]">
          <TabsTrigger value="match">Match Team</TabsTrigger>
          <TabsTrigger value="insights">AI Insights</TabsTrigger>
        </TabsList>
        
        <TabsContent value="match" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="h-5 w-5" />
                  AI Team Matcher
                </CardTitle>
                <CardDescription>
                  Describe your project requirements and we'll find the ideal team
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <Textarea
                    placeholder="Describe your project needs, technologies, timeline, and any specific skills required..."
                    className="min-h-[120px]"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    disabled={isLoading}
                  />
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? 'Matching...' : 'Find Ideal Team'}
                  </Button>
                </form>
              </CardContent>
              <CardFooter>
                <div className="w-full">
                  <p className="text-sm text-muted-foreground mb-2">Try these example prompts:</p>
                  <div className="flex flex-wrap gap-2">
                    {examplePrompts.map((examplePrompt, index) => (
                      <Badge 
                        key={index} 
                        variant="outline" 
                        className="cursor-pointer hover:bg-accent"
                        onClick={() => setPrompt(examplePrompt)}
                      >
                        {examplePrompt}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardFooter>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">AI Matching Tips</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <h3 className="font-medium flex items-center gap-1">
                    <Lightbulb className="h-4 w-4" /> Be Specific
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Include technologies, methodologies, and project scope for better matching.
                  </p>
                </div>
                <div className="space-y-2">
                  <h3 className="font-medium flex items-center gap-1">
                    <Lightbulb className="h-4 w-4" /> Mention Constraints
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Time constraints, budget limitations, and team size affect recommendations.
                  </p>
                </div>
                <div className="space-y-2">
                  <h3 className="font-medium flex items-center gap-1">
                    <Lightbulb className="h-4 w-4" /> Consider Team Dynamics
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Mention preferred work styles and collaboration approaches.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {matchedTeam.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Recommended Team Members</CardTitle>
                <CardDescription>
                  Based on your requirements, here are the most suitable team members
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-4">
                    {matchedTeam.map((member) => (
                      <div 
                        key={member.id} 
                        className="flex items-start space-x-4 p-4 rounded-lg border border-border bg-card hover:bg-accent/5 transition-colors"
                      >
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={member.avatar} alt={member.name} />
                          <AvatarFallback>{member.name.slice(0, 2)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 space-y-1">
                          <div className="flex justify-between">
                            <h3 className="font-medium">{member.name}</h3>
                            <span className="text-sm font-medium">
                              {member.compatibility}% Match
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground">{member.role}</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {member.skills.map((skill, index) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                {skill}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        <TabsContent value="insights">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="h-5 w-5" />
                AI Team Building Insights
              </CardTitle>
              <CardDescription>
                Data-driven recommendations to build effective teams
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <h3 className="text-lg font-medium">Skill Distribution Analysis</h3>
                <p className="text-muted-foreground">
                  Your organization shows strengths in frontend development and UX design, but may need more backend expertise. Consider upskilling team members in database optimization and API security.
                </p>
              </div>
              <div className="space-y-3">
                <h3 className="text-lg font-medium">Team Diversity Recommendations</h3>
                <p className="text-muted-foreground">
                  Research shows diverse teams produce more innovative solutions. Your current team composition could benefit from more diverse perspectives and experience levels.
                </p>
              </div>
              <div className="space-y-3">
                <h3 className="text-lg font-medium">Project Success Patterns</h3>
                <p className="text-muted-foreground">
                  Based on historical data, teams with a mix of specialists and generalists tend to deliver projects on time more consistently. Consider this balance when forming new teams.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
